/* ==========================================================================
   Bloomkind theme — core scripts
   Vanilla JS, no dependencies. Uses Shopify's documented Cart AJAX API and
   Section Rendering API. Every component is a custom element so it can
   initialise itself when the theme editor re-renders a section.
   ========================================================================== */
(() => {
  'use strict';

  const BK = (window.Bloomkind = window.Bloomkind || {});
  const routes = BK.routes || {};
  const strings = BK.strings || {};
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ---------- Helpers ---------- */
  BK.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  BK.announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  BK.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  BK.trapFocus = (container) => {
    BK.releaseFocus();
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    BK._trap = handler;
    const target = container.querySelector('[data-autofocus]') || container.querySelector(FOCUSABLE);
    if (target) target.focus({ preventScroll: true });
  };
  BK.releaseFocus = () => {
    if (BK._trap) document.removeEventListener('keydown', BK._trap);
    BK._trap = null;
  };

  /* ---------- Cart API ---------- */
  const cartSections = () => {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    document.querySelectorAll('cart-items[data-section-id]').forEach((el) => {
      if (!ids.includes(el.dataset.sectionId)) ids.push(el.dataset.sectionId);
    });
    return ids;
  };

  const handleResponse = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  BK.cart = {
    async add(formData) {
      const sections = cartSections();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await handleResponse(response);
      BK.cart.render(data.sections);
      document.dispatchEvent(new CustomEvent('bk:cart:added', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname })
      });
      const data = await handleResponse(response);
      BK.cart.render(data.sections);
      return data;
    },
    async update(payload) {
      const response = await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      return handleResponse(response);
    },
    /* Replace drawer / cart page markup with server-rendered HTML. */
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = BK.parseHTML(html);
        const count = doc.querySelector('[data-cart-count-value]');
        if (count) BK.cart.setCount(parseInt(count.dataset.cartCountValue, 10) || 0);
        const fresh = doc.querySelector('[data-cart-root]');
        const host = id === 'cart-drawer'
          ? document.querySelector('#CartDrawer [data-cart-root]')
          : document.querySelector(`cart-items[data-section-id="${id}"] [data-cart-root]`);
        if (!fresh || !host) return;
        const hadFocus = host.contains(document.activeElement);
        const focusKey = hadFocus && document.activeElement.closest('[data-line]') ? document.activeElement.closest('[data-line]').dataset.key : null;
        host.replaceWith(fresh);
        if (hadFocus) {
          // Keep keyboard users in place after the markup is replaced.
          const line = focusKey && fresh.querySelector(`[data-key="${CSS.escape(focusKey)}"] [data-qty-input]`);
          const target = line || fresh.querySelector('[data-drawer-close], a, button');
          if (target) target.focus({ preventScroll: true });
        }
      });
    },
    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.querySelectorAll('[data-cart-link]').forEach((el) => {
        el.setAttribute('aria-label', `${el.dataset.label || 'Cart'} (${count})`);
      });
    }
  };

  /* ---------- Generic drawer ---------- */
  class BkDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.onKey = (event) => { if (event.key === 'Escape' && this.classList.contains('is-open')) this.close(); };
      document.addEventListener('keydown', this.onKey);
    }
    disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('is-locked');
        const panel = this.querySelector('.drawer__panel');
        if (panel) BK.trapFocus(panel);
      });
      this.setAttribute('aria-hidden', 'false');
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      BK.releaseFocus();
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  define('bk-drawer', BkDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || !drawer.open) return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* Cart icon opens the drawer when it exists (falls back to /cart link). */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-link]');
    const drawer = document.getElementById('CartDrawer');
    if (!link || !drawer || !drawer.open) return;
    event.preventDefault();
    drawer.open(link);
  });

  /* ---------- Cart line items (drawer + cart page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-qty-change], [data-remove]');
        if (!btn) return;
        event.preventDefault();
        const item = btn.closest('[data-line]');
        const input = item.querySelector('[data-qty-input]');
        let qty = btn.hasAttribute('data-remove') ? 0 : (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyChange, 10);
        qty = Math.max(0, qty);
        this.updateLine(item, qty);
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-qty-input]');
        if (input) this.updateLine(input.closest('[data-line]'), Math.max(0, parseInt(input.value, 10) || 0));
        const note = event.target.closest('[name="note"]');
        if (note) BK.cart.update({ note: note.value }).catch(() => {});
      });
    }
    async updateLine(item, quantity) {
      if (!item) return;
      item.classList.add('is-updating');
      try {
        await BK.cart.change(parseInt(item.dataset.line, 10), quantity);
        BK.announce(strings.cartUpdated);
      } catch (error) {
        item.classList.remove('is-updating');
        const slot = item.querySelector('[data-line-error]');
        if (slot) { slot.textContent = error.message || strings.quantityError; slot.hidden = false; }
      }
    }
  }
  define('cart-items', CartItems);

  /* ---------- Product form (product page, quick add, sticky bar, upsell) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
    async onSubmit(event) {
      if (BK.cartType === 'page' && !document.getElementById('CartDrawer')) return; // native POST to /cart/add
      event.preventDefault();
      const button = event.submitter && event.submitter.matches('[type="submit"]') ? event.submitter : this.form.querySelector('[type="submit"]');
      if (button && button.getAttribute('aria-disabled') === 'true') return;
      const errorSlot = this.querySelector('[data-form-error]');
      if (errorSlot) errorSlot.hidden = true;
      if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
      try {
        await BK.cart.add(new FormData(this.form));
        BK.announce(strings.added);
        const drawer = document.getElementById('CartDrawer');
        if (drawer && BK.cartType === 'drawer') drawer.open(button);
        else window.location.href = routes.cart;
      } catch (error) {
        if (errorSlot) { errorSlot.textContent = error.message; errorSlot.hidden = false; }
        else window.alert(error.message);
      } finally {
        if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
      }
    }
  }
  define('product-form', ProductForm);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-step]');
        if (!btn || !this.input) return;
        event.preventDefault();
        const min = parseInt(this.input.min, 10) || 1;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + parseInt(btn.dataset.step, 10)));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  define('quantity-input', QuantityInput);

  /* ---------- Header: sticky on scroll-up ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      if (this.mode === 'none') return;
      this.lastY = window.scrollY;
      this.onScroll = () => {
        const y = window.scrollY;
        const h = this.offsetHeight;
        if (this.mode === 'scroll-up') {
          const hide = y > this.lastY && y > h * 2;
          this.classList.toggle('is-hidden', hide);
          if (hide) this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
        }
        this.lastY = y;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() { if (this.onScroll) window.removeEventListener('scroll', this.onScroll); }
  }
  define('sticky-header', StickyHeader);

  /* Close dropdown menus on outside click / Escape, and keep only one open. */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details.nav-dropdown[open]').forEach((d) => { if (!d.contains(event.target)) d.removeAttribute('open'); });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('details.nav-dropdown[open]');
    if (open) { open.removeAttribute('open'); const s = open.querySelector('summary'); if (s) s.focus(); }
  });
  document.addEventListener('toggle', (event) => {
    const d = event.target;
    if (!(d instanceof HTMLDetailsElement) || !d.classList.contains('nav-dropdown') || !d.open) return;
    document.querySelectorAll('details.nav-dropdown[open]').forEach((o) => { if (o !== d) o.removeAttribute('open'); });
  }, true);

  /* ---------- Search modal + predictive search ---------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => { if (event.target.closest('[data-search-close]')) this.close(); });
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener;
      this.hidden = false;
      document.body.classList.add('is-locked');
      BK.trapFocus(this.querySelector('.search-modal__panel'));
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.body.classList.remove('is-locked');
      BK.releaseFocus();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
  }
  define('search-modal', SearchModal);
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-search-open]');
    const modal = document.querySelector('search-modal');
    if (!opener || !modal) return;
    event.preventDefault();
    modal.open(opener);
  });

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || !routes.predictiveSearch) return;
      this.cache = new Map();
      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', BK.debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (event) => this.onKey(event));
    }
    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) { this.close(); return; }
      if (this.controller) this.controller.abort();
      if (this.cache.has(term)) { this.show(this.cache.get(term)); return; }
      this.controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const markup = BK.parseHTML(html).querySelector('[data-predictive-markup]');
        const out = markup ? markup.innerHTML : '';
        this.cache.set(term, out);
        this.show(out);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }
    show(html) {
      this.results.innerHTML = html;
      this.results.hidden = !html.trim();
      this.input.setAttribute('aria-expanded', String(!this.results.hidden));
    }
    close() {
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
    }
    onKey(event) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key) || this.results.hidden) return;
      const items = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!items.length) return;
      const current = this.results.querySelector('[aria-selected="true"]');
      let index = items.indexOf(current);
      if (event.key === 'Enter') {
        if (current) { event.preventDefault(); window.location.href = current.href; }
        return;
      }
      event.preventDefault();
      index = event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length;
      items.forEach((el) => el.setAttribute('aria-selected', 'false'));
      items[index].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', items[index].id);
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ---------- Carousel (scroll-snap + arrows + dots) ---------- */
  class BkCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.center = this.hasAttribute('data-center');
      if (this.center) {
        // Tapping a side slide brings it to the centre instead of activating it.
        this.track.addEventListener('click', (event) => {
          const slide = event.target.closest('[data-track] > *');
          if (!slide || slide.classList.contains('is-active')) return;
          event.preventDefault();
          event.stopPropagation();
          this.goTo(this.slides().indexOf(slide));
        }, true);
      }
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', BK.debounce(() => this.update(), 60), { passive: true });
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this.track);
      if (this.dataset.autoplay) this.startAutoplay(parseInt(this.dataset.autoplay, 10) * 1000);
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); clearInterval(this.timer); }
    slides() { return Array.from(this.track.children).filter((el) => el.offsetParent !== null || el.getClientRects().length); }
    pageCount() {
      const slides = this.slides();
      if (!slides.length) return 0;
      if (this.center) return slides.length;
      const perView = Math.max(1, Math.round(this.track.clientWidth / slides[0].getBoundingClientRect().width));
      return Math.max(1, Math.ceil(slides.length - perView + 1));
    }
    build() {
      const pages = this.pageCount();
      const fits = this.track.scrollWidth <= this.track.clientWidth + 4;
      this.classList.toggle('carousel--fit', fits);
      // Scrollable regions must be reachable by keyboard (WCAG 2.1.1).
      if (!fits && !this.track.querySelector(FOCUSABLE)) {
        this.track.tabIndex = 0;
        if (!this.track.hasAttribute('aria-label')) this.track.setAttribute('aria-label', this.dataset.slideLabel || 'Carousel');
      }
      if (this.dotsWrap) {
        this.dotsWrap.innerHTML = '';
        if (!fits) for (let i = 0; i < pages; i += 1) {
          const li = document.createElement('li');
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'carousel__dot';
          b.setAttribute('aria-label', `${this.dataset.slideLabel || 'Slide'} ${i + 1}`);
          b.addEventListener('click', () => this.goTo(i));
          li.appendChild(b);
          this.dotsWrap.appendChild(li);
        }
      }
      if (this.center && !this.started) {
        // Start on the middle slide so there is a slide peeking on each side.
        this.started = true;
        const slides = this.slides();
        const mid = Math.floor((slides.length - 1) / 2);
        if (slides[mid]) this.track.scrollTo({ left: this.leftFor(slides[mid]), behavior: 'auto' });
      }
      this.update();
    }
    leftFor(slide) {
      const base = slide.offsetLeft - this.track.offsetLeft;
      return this.center ? base - (this.track.clientWidth - slide.offsetWidth) / 2 : base;
    }
    index() {
      const slides = this.slides();
      if (!slides.length) return 0;
      if (this.center) {
        const mid = this.track.scrollLeft + this.track.clientWidth / 2;
        let best = 0; let dist = Infinity;
        slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - this.track.offsetLeft + s.offsetWidth / 2 - mid); if (d < dist) { dist = d; best = i; } });
        return best;
      }
      const left = this.track.scrollLeft;
      let best = 0; let dist = Infinity;
      slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - this.track.offsetLeft - left); if (d < dist) { dist = d; best = i; } });
      return best;
    }
    goTo(i) {
      const slides = this.slides();
      const target = slides[Math.max(0, Math.min(i, slides.length - 1))];
      if (target) this.track.scrollTo({ left: this.leftFor(target), behavior: 'smooth' });
    }
    go(dir) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.center) {
        const n = this.slides().length;
        let next = this.index() + dir;
        if (this.dataset.loop) next = (next + n) % n;
        this.goTo(next);
        return;
      }
      if (dir > 0 && this.track.scrollLeft >= max - 4 && this.dataset.loop) { this.goTo(0); return; }
      this.goTo(this.index() + dir);
    }
    update() {
      const i = this.index();
      const max = this.track.scrollWidth - this.track.clientWidth;
      const count = this.slides().length;
      if (this.center) {
        if (this.prev) this.prev.disabled = i === 0 && !this.dataset.loop;
        if (this.next) this.next.disabled = i === count - 1 && !this.dataset.loop;
        this.slides().forEach((s, n) => {
          s.classList.toggle('is-active', n === i);
          if (n !== i) s.querySelectorAll('video').forEach((v) => { if (!v.muted) v.pause(); });
        });
      } else {
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4 && !this.dataset.loop;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max - 4 && !this.dataset.loop;
      }
      if (this.dotsWrap) {
        const dots = this.dotsWrap.querySelectorAll('button');
        const active = this.center ? i : (this.track.scrollLeft >= max - 4 ? dots.length - 1 : Math.min(i, dots.length - 1));
        dots.forEach((d, n) => d.setAttribute('aria-current', n === active ? 'true' : 'false'));
      }
      this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: i } }));
    }
    startAutoplay(ms) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const tick = () => this.go(1);
      const start = () => { clearInterval(this.timer); this.timer = setInterval(tick, ms); };
      const stop = () => clearInterval(this.timer);
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      start();
    }
  }
  define('bk-carousel', BkCarousel);

  /* ---------- Estimated arrival (working days, calculated in the browser) ---------- */
  BK.addWorkingDays = (days) => {
    const date = new Date();
    let left = Number(days) || 0;
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) left -= 1;
    }
    return date;
  };
  class DeliveryRange extends HTMLElement {
    connectedCallback() {
      let fmt;
      try { fmt = new Intl.DateTimeFormat(this.dataset.locale || 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(min, Number(this.dataset.max) || 0);
      const from = fmt.format(BK.addWorkingDays(min));
      const to = fmt.format(BK.addWorkingDays(max));
      this.textContent = from === to ? from : `${from} – ${to}`;
    }
  }
  define('delivery-range', DeliveryRange);

  /* ---------- Cart recommendations (Product Recommendations API) ---------- */
  const recsCache = new Map();
  class CartRecommendations extends HTMLElement {
    async connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const key = `${url}|${this.dataset.key || ''}`;
      try {
        let html = recsCache.get(key);
        if (html === undefined) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(response.status);
          const list = BK.parseHTML(await response.text()).querySelector('[data-cart-recs]');
          html = list && list.children.length ? list.outerHTML : '';
          recsCache.set(key, html);
        }
        if (!html || !this.isConnected) return;
        this.querySelector('[data-recs-slot]').innerHTML = html;
        this.hidden = false;
      } catch (error) {
        this.remove();
      }
    }
  }
  define('cart-recommendations', CartRecommendations);

  /* ---------- Announcement rotator ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__item'));
      if (this.items.length < 2) return;
      this.i = 0;
      const ms = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => {
        if (this.matches(':hover, :focus-within')) return;
        this.items[this.i].setAttribute('aria-hidden', 'true');
        this.i = (this.i + 1) % this.items.length;
        this.items[this.i].setAttribute('aria-hidden', 'false');
      }, ms);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  define('announcement-bar', AnnouncementBar);

  /* ---------- Back to top ---------- */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.querySelector('button').addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      const onScroll = () => { this.hidden = window.scrollY < 600; };
      window.addEventListener('scroll', BK.debounce(onScroll, 80), { passive: true });
      onScroll();
    }
  }
  define('back-to-top', BackToTop);

  /* ---------- Localization selector: submit on change ---------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-localization-select]');
    if (select && select.form) select.form.submit();
  });

  /* ---------- Deferred video (YouTube/Vimeo/hosted) ---------- */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-load-media]');
      if (btn) btn.addEventListener('click', () => this.load());
    }
    load() {
      if (this.loaded) return;
      const tpl = this.querySelector('template');
      if (!tpl) return;
      this.loaded = true;
      const content = tpl.content.firstElementChild.cloneNode(true);
      this.querySelector('[data-load-media]').replaceWith(content);
      if (content.tagName === 'VIDEO') { content.play().catch(() => {}); }
    }
  }
  define('deferred-media', DeferredMedia);

  /* Pause other videos when one starts playing. */
  document.addEventListener('play', (event) => {
    if (event.target.tagName !== 'VIDEO' || event.target.muted) return;
    document.querySelectorAll('video').forEach((v) => { if (v !== event.target && !v.muted) v.pause(); });
  }, true);

  /* ---------- Theme editor hooks ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => document.body.classList.remove('is-locked'));
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-track] > *');
      const carousel = event.target.closest('bk-carousel');
      if (slide && carousel) carousel.goTo(Array.from(slide.parentElement.children).indexOf(slide));
    });
  }
})();
